import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { FINANCE_NAV } from './nav';

interface Account { id: number; principal_type: string; principal_id: string; tier: string; tier_label: string; tier_started_at: string; }
interface Dist { tier: string; label: string; count: number; }

const TIERS = ['essential', 'professional', 'practice', 'network', 'enterprise'];

const Subscriptions: React.FC = () => {
    const { api } = useInternalApi();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [distribution, setDistribution] = useState<Dist[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Subscriptions & Plans - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('admin-dashboard/finance/subscriptions/');
            if (res.data?.status === 'success') {
                setAccounts(res.data.data.accounts || []);
                setDistribution(res.data.data.distribution || []);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const changeTier = async (a: Account, tier: string) => {
        try {
            await api.patch(`admin-dashboard/finance/subscriptions/${a.id}/`, { tier });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update tier.' });
        }
    };

    return (
        <InternalShell activeDashboard="finance_center" subNav={FINANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />Subscriptions & Plans</h2>
            <p className="text-muted mb-4">Every customer principal's subscription tier</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="d-flex gap-2 mb-4 flex-wrap">
                        {distribution.map(d => (
                            <span key={d.tier} className="badge bg-light text-dark p-2 border">{d.label}: {d.count}</span>
                        ))}
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {accounts.length === 0 ? (
                                <div className="text-center py-5 text-muted">No subscription accounts yet.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Principal</th><th>Tier</th><th>Since</th><th>Change Tier</th></tr></thead>
                                    <tbody>
                                        {accounts.map(a => (
                                            <tr key={a.id}>
                                                <td>{a.principal_type}:{a.principal_id}</td>
                                                <td><span className="badge bg-primary">{a.tier_label}</span></td>
                                                <td>{new Date(a.tier_started_at).toLocaleDateString()}</td>
                                                <td>
                                                    <select className="form-select form-select-sm" style={{ width: 160 }} value={a.tier} onChange={(e) => changeTier(a, e.target.value)}>
                                                        {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default Subscriptions;
